import { type DocumentNode, gql, useLazyQuery, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Typography } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect, useState } from 'react';
import allActions, { type AllActionsResponse, type AllActionsVariables } from 'src/api/actions/allActions';
import sendHires, { type SendHiresResponse, type SendHiresVariables } from 'src/api/actions/hiresRequest';
import { COLLECTION, HIRES_ACTION_TYPE, SELECTED_ITEMS } from 'src/constants';
import { buildAllItemsQuery } from 'src/dynamoQueries/items';
import viewStateVar from 'src/store/viewState';
import AkomiDialog from './AkomiDialog';
import AkomiDialogActions from './AkomiDialogActions';

const MAX = 25;

interface HiresDialogProps {
  isOpen: boolean;
  isSingleAssetOnMediaPage: boolean;
}

interface AllItemsQueryResponse {
  allItems?: {
    totalCount: number;
  };
}

interface HiresErrorItem {
  asset: string;
  error: string;
}

const HiresDialog: React.FC<HiresDialogProps> = ({ isOpen, isSingleAssetOnMediaPage }) => {
  const viewState = useReactiveVar(viewStateVar);
  const [hiresActionId, setHiresActionId] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [messageExpanded, setMessageExpanded] = useState<boolean>(false);
  const [errors, setErrors] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);

  const buildQuery = (): DocumentNode => {
    const args = {
      container_Name: viewState.selectedCollectionId || undefined,
    };
    const q = buildAllItemsQuery(args);
    delete (q.query.allItems as Record<string, unknown>).edges;
    const gqlQuery = jsonToGraphQLQuery(q);
    return gql(gqlQuery);
  };

  const [getCollectionCount] = useLazyQuery<AllItemsQueryResponse>(buildQuery(), {
    onCompleted: (data) => {
      if (isSingleAssetOnMediaPage) {
        setTotalCount(1);
      } else {
        setTotalCount(data.allItems?.totalCount ?? 0);
      }
    },
  });

  const { loading: loadingActions } = useQuery<AllActionsResponse, AllActionsVariables>(allActions, {
    variables: {
      type: HIRES_ACTION_TYPE,
    },
    onCompleted: (data) => {
      const actionId = data?.allActions?.edges?.map((e) => e.node.id)[0] ?? null;
      setHiresActionId(actionId);
    },
  });

  const [handleSendHires, { loading: sendingHires }] = useMutation<SendHiresResponse, SendHiresVariables>(sendHires, {
    variables: {
      actionId: hiresActionId ?? undefined,
      payload: {
        assetIds: viewState.hiresDialog === SELECTED_ITEMS ? viewState.selectedAssets : null,
        collection: viewState.hiresDialog === COLLECTION ? viewState.selectedCollectionId : null,
      },
    },
    onCompleted: () => {
      setSuccess(true);
    },
    onError: (err) => {
      try {
        const parsedErrors = JSON.parse(err.message) as HiresErrorItem[];
        const errorStr = parsedErrors.map((e) => `${e.asset}: ${e.error}`).join(', ');
        setErrors(errorStr);
      } catch {
        // If the error is not in JSON format, store it instead of parsing
        setErrors(err.message);
      }
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally run only once on mount to set initial total count
  useEffect(() => {
    if (viewState.hiresDialog === SELECTED_ITEMS) {
      setTotalCount(viewState.selectedAssets.length);
    } else {
      getCollectionCount();
    }
  }, []);

  const sendHiresRequestHandler = async (): Promise<void> => {
    handleSendHires();
  };

  const cancelHiresRequestHandler = (): void => {
    if (errors || success) {
      if (viewState.selectedCollectionId) {
        viewStateVar({
          ...viewStateVar(),
          hiresDialog: null,
          showLeftSidebar: false,
          showRightSidebar: false,
          selectedCollectionId: '',
          openEditCollection: false,
        });
      } else {
        viewStateVar({
          ...viewStateVar(),
          hiresDialog: null,
          isSelectAll: false,
        });
      }
    } else {
      viewStateVar({
        ...viewStateVar(),
        hiresDialog: null,
        isSelectAll: false,
      });
    }
  };

  const expandMessageHandler = (): void => {
    setMessageExpanded((prev) => !prev);
  };

  // Too many items selected — early return with informational dialog
  if (totalCount > MAX) {
    return (
      <AkomiDialog
        title="HiRes Request"
        maxWidth="xs"
        open={isOpen}
        onClose={cancelHiresRequestHandler}
        actions={<AkomiDialogActions variant="close" onPrimary={cancelHiresRequestHandler} primaryLabel="OK" />}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            justifyContent: 'center',
            alignItems: 'center',
            py: 2,
          }}
        >
          <Typography>You have more than the maximum number of items selected.</Typography>
          <Typography>{`Maximum Items: ${MAX}`}</Typography>
          <Typography>{`Items Selected: ${totalCount}`}</Typography>
        </Box>
      </AkomiDialog>
    );
  }

  // Determine which action buttons to show based on state
  const renderActions = (): React.ReactNode => {
    // After success or error — just a close button
    if (success || errors) {
      return <AkomiDialogActions variant="close" onPrimary={cancelHiresRequestHandler} primaryLabel="Close" />;
    }
    // Normal state — Continue + Cancel (with submitting spinner when sending)
    return (
      <AkomiDialogActions
        variant="cancel-confirm"
        primaryLabel="Continue"
        secondaryLabel="Cancel"
        onPrimary={sendHiresRequestHandler}
        onSecondary={cancelHiresRequestHandler}
        primaryDisabled={loadingActions}
        submitting={sendingHires}
      />
    );
  };

  return (
    <AkomiDialog
      title="HiRes Request"
      maxWidth="xs"
      open={isOpen}
      onClose={cancelHiresRequestHandler}
      actions={renderActions()}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
        }}
      >
        {success && <Typography>Request Sent.</Typography>}
        {errors && <Typography>Request Failed.</Typography>}
        {!success && !errors && (
          <Typography>
            {totalCount === 1
              ? 'You have requested hires delivery of 1 file. Continue?'
              : `You have requested hires delivery of ${totalCount} files. Continue?`}
          </Typography>
        )}
      </Box>
      {errors && (
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              p: '10px',
              alignItems: 'center',
              justifyContent: 'right',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Typography>Details</Typography>
            <IconButton onClick={expandMessageHandler} size="large">
              {messageExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={messageExpanded}>
            <Box
              sx={{
                overflowY: 'auto',
                maxHeight: '400px',
              }}
            >
              <Typography
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  p: '10px',
                  fontSize: 12,
                  textAlign: 'left',
                }}
              >
                {errors}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </AkomiDialog>
  );
};

export default HiresDialog;
